/**
 * 콘텐츠 상거래 계약 (v23, `docs/47-content-monetization-design.md`).
 *
 * **광고 브레이크 밖에서, 장면이 상품을 부른다.** 회차에 붙은 시점 메타데이터(`ProductCue`)
 * 가 「언제 어떤 분류를」 정하고, Offer Broker 가 「무엇을 팔지」를 제휴 e-commerce
 * 파트너에게 물어 답한다 (절대 규칙 62).
 *
 * ── 이 파일이 `ad.ts` 와 나란히 서는 이유 ────────────────────
 *
 * **v21 이 깔아 둔 관을 그대로 쓴다** — 리시버가 밖에서 봉투를 받아 BLOOM 을 지나 TV2M 에
 * 사본을 남기고 폰이 그것을 읽는 길이다. 새 경로를 만드는 것이 아니라 **이미 있던 경로에
 * 다른 출처를 꽂는 것**이고, 그래서 아래 타입들이 `AdItem`·`AdRequest`·`AdPolicy` 와
 * **같은 자리에 같은 뜻의 필드**를 갖는다. 나란히 놓고 읽을 것.
 *
 * **광고와 결정적으로 다른 것 하나 — 이것은 시청 시간을 늘리지 않는다.** 스트림을 자르지
 * 않으므로 회차 42분은 42분 그대로이고, 그래서 `ad.ts` 의 `effectiveDurationSec` 에
 * 해당하는 것이 **여기에는 없다** (절대 규칙 61·57). 그 이름이 이 경로의 코드에 나타나면
 * 그것이 곧 실수다.
 */
// ---------------------------------------------------------------------------
// 1. 분류 — 큐가 나르는 것은 상품이 아니라 코드다
// ---------------------------------------------------------------------------
/**
 * IAB **Ad Product Taxonomy** 버전. **문자열이 계약이다.**
 *
 * **지금 한 값뿐이고 그래서 더 필요하다** — 3.0 이 나오는 날 코드가 두 표를 함께 이해해야
 * 하고, 그때 `ProductCue.taxonomy` 가 어느 표의 코드인지 말해 주는 유일한 값이다.
 * 없으면 「1062」가 어느 표의 1062 인지 아무도 모른다.
 */
export const OFFER_TAXONOMY = 'ad-product-2.0';
/**
 * 콘솔의 선택 상자가 그릴 **데모용 부분집합** (`docs/47` §3.1).
 *
 * ★ **표시용이다. 검사에 쓰지 않는다.** 서버의 검사는 **「1000 이상의 정수」 하나**이고,
 * 여기 없는 코드는 **숫자 그대로 화면에 보인다** — 막지 않는다.
 *
 * **전량을 저장소에 넣지 않는 이유가 그것이다.** 표를 우리가 들고 검사하면 IAB 가 행을
 * 더하는 날 **그 코드가 우리 서버에서 거부되고**, 그때 사업자에게는 「분류가 왜 안 되나」로
 * 보인다. 전량 도입은 범위 밖이고 그 사실을 `docs/47` §15 가 적는다.
 */
export const OFFER_CATEGORY_LABELS = {
    /*
     * ★ **2026-09 IAB 원본 TSV 와 대조해 일곱을 정정했다** (v31 ST1 · `docs/61` §5.1).
     *
     * 처음 판은 이름을 그럴듯하게 짓고 번호를 붙였는데 **일곱이 실제 표와 달랐다** —
     * `1076` 은 「Antiques」, `1195` 는 「Tights」, `1201` 은 「Meal Kits」, `1283` 은
     * 「Housewares」, `1286` 은 「Lighting」, `1305` 는 「Primary Education」, `1311` 은
     * 「Telecommuting」이었다. **데모 안에서는 번호끼리 맞아 증상이 없었다** — 시드·카탈로그·
     * 이 표가 같은 틀린 번호를 써서 카드가 떴다. 자동 태깅은 진짜 표로 답하므로 정정 없이는
     * `1097`(Consumer Electronics)이 `1305` 와 만나지 않고, 진짜 제휴사가 붙는 날 `1305` 는
     * 「초등교육」으로 질의된다. `offer.test.ts` 의 「원본 표와 같은 이름」이 이것을 문다.
     */
    1058: 'Clothing and Accessories',
    1059: 'Clothing and Accessories › Clothing',
    1062: "Clothing and Accessories › Clothing › Men's Clothing",
    1067: "Clothing and Accessories › Clothing › Women's Clothing",
    1070: 'Clothing and Accessories › Footwear',
    1072: 'Clothing and Accessories › Handbags and Wallets',
    1073: 'Clothing and Accessories › Jewelry and Watches',
    1097: 'Consumer Electronics',
    1138: 'Consumer Packaged Goods › Cosmetics',
    1165: 'Consumer Packaged Goods › General Food',
    1268: 'Durable Goods › Furniture',
    1283: 'Durable Goods › Home and Garden Products › Housewares',
    1284: 'Durable Goods › Home and Garden Products › Kitchen and Dining Products',
    1289: 'Durable Goods › Household Appliances',
    1524: 'Sporting Goods',
};
/**
 * Offer Request 의 언어 축 (`docs/47` §7.2).
 *
 * ── ★ 설계에 빈 자리가 하나 있었고, 여기가 그 자리다 ──────────
 *
 * §7.2 는 이 값을 **「채널이 정한다」**(v21 D26 승계 — 기기의 UI 언어가 아니다)라고
 * 적는데, **§2.1 이 `Channel.offerLanguage` 컬럼을 만들지 않았다.** 그래서 이 확장에서
 * 「채널이 정하는 언어」로 쓸 수 있는 값은 **`Channel.adLanguage` 하나**뿐이고,
 * 리시버가 `adPolicy?.language ?? 'ko'` 로 채운다.
 *
 * **그 대용이 참인 자리와 아닌 자리가 있다.** 영어 드라마 채널에 영어 광고를 편성해
 * 두었다면 상품도 영어인 것이 옳다. **광고를 끈 영어 채널은 `ko` 로 떨어진다** —
 * 알려진 한계이고 `docs/48` 이 그것을 적는다. 컬럼을 하나 더 만드는 것이 그 답이며
 * **이 확장의 범위 밖이다**(정책 넷을 다섯으로 늘리는 일이다).
 *
 * **그래도 축을 `AdLanguage` 로 쓰지 않고 여기 따로 둔다** — 상품의 언어는 광고의
 * 언어가 아니고, 이름을 빌려 쓰면 컬럼이 생기는 날 **그 두 축이 이미 한 몸이 되어
 * 있다.**
 */
export const OFFER_LANGUAGES = ['ko', 'en'];
export function isOfferLanguage(value) {
    return OFFER_LANGUAGES.includes(value);
}
/** 없을 때의 값. **이 파라미터가 없던 세계는 정의상 한국어뿐이었다** (v21 D27 승계) */
export const OFFER_LANGUAGE_DEFAULT = 'ko';
/** 분류 코드의 최솟값. **IAB Ad Product Taxonomy 의 Unique ID 는 1000 부터다** */
export const OFFER_CATEGORY_ID_MIN = 1000;
/**
 * 분류 코드가 형식상 성립하는가. **표에 있는지는 보지 않는다** — 위 `OFFER_CATEGORY_LABELS`
 * 의 ★ 가 그 이유 전부다.
 */
export function isOfferCategoryId(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= OFFER_CATEGORY_ID_MIN;
}
/**
 * 분류 코드를 화면에 쓸 문자열로. **모르는 코드는 숫자 그대로 보인다.**
 *
 * **`data-i18n="data"` 를 붙이는 자리다** — 이 값은 사전을 거치지 않는 외부 데이터이고,
 * 붙이지 않으면 `verify-locale.mjs` 의 한글 훑기가 「번역 누락」으로 신고한다(절대 규칙 24).
 * 지금 라벨이 전부 영어라 훑기에 안 걸리지만 **그 사실에 기대지 않는다** — 표가 한국어로
 * 번역되는 날 조용히 깨진다.
 */
export function offerCategoryLabel(categoryId) {
    return OFFER_CATEGORY_LABELS[categoryId] ?? String(categoryId);
}
/**
 * ★ **이 회차에서 실제로 열릴 큐들. 산술이 한 벌이다** (`docs/47` §3.4 · v21 D15 승계).
 *
 * **SDK 코어가 아니라 계약 패키지에 둔다.** 같은 산술을 **셋**이 필요로 한다 —
 * 리시버(예약 판정) · `bloom-console`(「이 회차에 상품이 몇 번 뜨는가」 경고) ·
 * 브로커 테스트. 두 벌이 되면 **콘솔이 말하는 노출 수와 실제가 갈리고**, 그 어긋남은
 * **아무 오류도 내지 않는다.**
 *
 * **정책만으로 정해진다 — 재생을 모른다.** 그래서 순수 함수이고 node 에서 검증된다.
 *
 * ── `continue` 와 `break` 가 다른 자리에 있는 것이 이 함수의 전부다 ──
 *
 * - **꼬리에 걸린 큐** → `continue`. `startSec + displaySec > durationSec` 이면 카드가
 *   뜨자마자 회차가 넘어간다. **정책 화면에서는 옳아 보인다** — 「15초 유지」는 아무 문제
 *   없는 문장이고, 함정은 **그 회차 길이와 만나야** 생긴다 (v21 D30 이 미드롤 컷에 대해
 *   밟은 그것과 같은 모양이다).
 * - **간격에 걸린 큐** → **`continue` 이지 `break` 가 아니다.** `break` 로 두면 촘촘한 큐
 *   하나가 그 회차의 나머지를 **통째로 죽이고**, 증상은 「어떤 회차에만 상품이 안 뜬다」
 *   이며 **큐가 고르면 영원히 재현되지 않는다.** 하네스의 「바닥 검사」가 이것 하나를
 *   물려고 있다.
 * - **상한에 닿았다** → **`break`.** 그것이 상한의 뜻이다.
 *
 * **정렬이 함수 안에 있다.** 호출자가 정렬해 넘길 것이라 기대하면, 정렬하지 않은 호출자
 * 하나가 생기는 날 간격 판정이 **입력 순서에 따라 다른 답**을 낸다.
 */
export function activeCues(policy, cues, itemDurationSec) {
    if (policy.displaySec <= 0)
        return [];
    const sorted = [...cues].filter((c) => c.isActive).sort((a, b) => a.startSec - b.startSec);
    const out = [];
    let lastClosed = Number.NEGATIVE_INFINITY;
    for (const cue of sorted) {
        // 꼬리에 붙는 큐를 **버린다. 자르는 것이 아니다.**
        if (cue.startSec + policy.displaySec > itemDurationSec)
            continue;
        // 최소 간격 — **거르는 것이지 미루는 것이 아니다** (v21 §3.4 승계).
        if (cue.startSec < lastClosed + policy.minGapSec)
            continue;
        out.push(cue);
        lastClosed = cue.startSec + policy.displaySec;
        // 상한에 닿으면 멈춘다. **위 둘과 달리 여기만 `break` 다.**
        if (policy.maxPerItem > 0 && out.length >= policy.maxPerItem)
            break;
    }
    return out;
}
/**
 * 이 회차에서 **버려진** 큐의 수. 콘솔이 「큐 N건 중 M건만 뜹니다」를 그리는 근거다.
 *
 * **산술을 콘솔에 다시 쓰지 않으려고 여기 둔다** (v21 의 `droppedTailCueCount` 와 같은
 * 자리·같은 이유). 운영자는 자기가 넣은 큐가 그 회차에서 몇 번 뜨는지 알 수 없고,
 * **결과는 옳지만 왜 그런지는 화면 어디에도 없다.**
 */
export function droppedCueCount(policy, cues, itemDurationSec) {
    const candidates = cues.filter((c) => c.isActive).length;
    return candidates - activeCues(policy, cues, itemDurationSec).length;
}
/**
 * Offer Request → URL. **순수 함수다.**
 *
 * ── 왜 `packages/contracts` 인가 — v21 과 갈리는 자리 ─────────
 *
 * v21 의 짝(`buildTagUrl`)은 **SDK 안에** 있다. 광고에는 이 함수를 부르는 자리가
 * **리시버 하나뿐**이었기 때문이다.
 *
 * **v23 에는 둘이다** — 리시버, 그리고 **`preview=1` 을 실은 BLOOM Server 의 미리보기**
 * (`POST …/cues/preview` · `docs/47` §5.1). 서버는 SDK 의 웹 어댑터를 import 할 수 없으므로
 * (`fetch`·DOM 전제다) 그대로 두면 **URL 조립이 두 벌**이 되고, 그때 어긋남은
 * **「미리보기로 본 것과 실제로 나가는 것이 다르다」**이며 **아무 오류도 내지 않는다** —
 * `activeCues` 를 여기 둔 것과 정확히 같은 이유다 (v21 D15 · 절대 규칙 63).
 *
 * **`URL` 을 쓰지 않는다** — 이 패키지는 `lib: ES2022`·`types: []` 로 컴파일되어 그것이
 * 없다. `buildTrackingUrl` 이 같은 이유로 손으로 조립한다.
 *
 * **같은 입력에 같은 출력이다** — `correlator` 가 인자이므로 성립하고, 그래야 「매번
 * 다르다」를 단위 테스트가 못박을 수 있다.
 */
export function buildOfferUrl(req) {
    const base = req.brokerUrl.replace(/\/+$/, '');
    const params = [
        ['cat', String(req.categoryId)],
        ['cattax', req.taxonomy],
        ['ch', req.channelId],
        ['ep', req.productId],
        ['cue', req.cueId],
        ['pos', String(Math.max(0, Math.floor(req.positionSec)))],
        ['n', String(OFFER_MAX)],
        ['cur', req.currency],
        ['lang', req.lang],
        ['dev', req.deviceId],
        ['pid', req.playlistId],
        ['correlator', String(req.correlator)],
    ];
    /*
     * **`hint` 는 없으면 아예 안 싣는다.** 빈 값을 실으면 브로커가 「빈 조언」과 「조언 없음」을
     * 구분해야 하고, 그 구분에는 뜻이 없다 — 제휴사는 어차피 무시해도 되는 값이다(D5).
     */
    if (req.hint !== null && req.hint !== '')
        params.push(['hint', req.hint]);
    // **미리보기는 참일 때만 싣는다.** `preview=0` 을 실으면 「거짓을 명시했다」와
    // 「안 보냈다」가 갈리고, 브로커가 둘을 같게 다뤄야 할 이유가 없다.
    if (req.preview === true)
        params.push(['preview', '1']);
    const query = params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return `${base}/offers?${query}`;
}
// ---------------------------------------------------------------------------
// 6. 상수
// ---------------------------------------------------------------------------
/**
 * 예약형 요청 — 큐 시점보다 이만큼 앞서 쏜다.
 *
 * **`onTimeUpdate` 축(4Hz)에서 돈다** — 회차 시작 자리에서 요청하면 42분짜리 회차에서
 * 큐보다 42분 앞서 묻게 된다 (v21 §8.4 의 ★ 가 배운 그것).
 */
export const OFFER_REQUEST_LEAD_SEC = 10;
/**
 * ★ **`OFFER_REQUEST_LEAD_SEC * 1000` 보다 짧아야 한다** (`docs/47` §8.4 · §14 의 8).
 *
 * 길게 두면 응답이 오는 순간 이미 시점이 지나 있고, 코어의 「늦은 응답」 가드가 **매번
 * 버리게 되어 상품이 한 번도 안 뜨는데 요청·응답은 정상으로 보인다.**
 *
 * **그 관계를 지키는 것은 `offer.test.ts` 의 단언 한 줄뿐이다** — 다른 아무도 재지 않는다.
 */
export const OFFER_REQUEST_TIMEOUT_MS = 5_000;
/** 한 요청이 받아 오는 최대 개수. **지금은 언제나 1 이다** (`docs/47` D4) */
export const OFFER_MAX = 1;
/** `ProductCue.hint` 의 최대 길이 */
export const OFFER_HINT_MAX = 80;
//# sourceMappingURL=offer.js.map